import * as THREE from 'three'
import type { EntityType } from '@/plugins/cad/module/CADModule/model/common/constant'
import type { IEntity } from '@/plugins/cad/module/CADModule/model/entity'
import type { MaterialManager } from '@/plugins/cad/module/CADModule/object/material/MaterialManager'
import { installLineTypeDashScaleUpdater } from './lineTypeDashScaleUpdater'

/**
 * 实体高亮状态枚举，用于区分 hover/select/none 三种渲染态。
 */
export enum HighlightMode {
  /** 鼠标悬停的预选态 */
  Hover = 'hover',
  /** 已选中态 */
  Select = 'select',
  /** 未高亮（普通显示） */
  None = 'none',
}

/**
 * 实体刷新选项，控制 updateEntity 时需要重建的内容。
 */
export interface IUpdateOptions {
  /** 是否重建几何体 */
  updateGeometry?: boolean
  /** 是否重建材质 */
  updateMaterial?: boolean
}

/**
 * 实体对象管理器基类，提供实体到 Three 对象的映射以及高亮/生命周期管理。
 *
 * @typeParam T - 管理的实体类型
 * @typeParam U - 对应的渲染对象类型
 */
export abstract class BaseObjectManager<
  T extends IEntity = IEntity,
  U extends THREE.Object3D = THREE.Object3D,
> {
  protected objectMap: Map<string, U> = new Map()
  protected group: THREE.Group = new THREE.Group()

  // hover对象集合(预选)
  protected hoverEntityIds: Set<string> = new Set()
  // select对象集合(选中)
  protected selectedEntityIds: Set<string> = new Set()
  protected hoverMaterialOption = {
    color: 0xffff00,
    depthTest: false,
    transparent: true,
    opacity: 0.8,
  }
  protected selectMaterialOption = {
    color: 0x00ffff,
    depthTest: false,
    transparent: true,
    opacity: 0.8,
  }
  protected materialManager: MaterialManager

  constructor(parent: THREE.Object3D, materialManager: MaterialManager, entityType: EntityType) {
    this.materialManager = materialManager
    this.group.userData.entityType = entityType
    parent.add(this.group)
  }

  /**
   * 为实体创建渲染对象并加入场景。
   *
   * @param entity - 目标实体
   * @returns 与实体绑定的渲染对象
   */
  addEntity(entity: T) {
    const object = this.createObject(entity)
    object.userData.entity = entity
    installLineTypeDashScaleUpdater(object)
    this.group.add(object)
    this.objectMap.set(entity.id, object)
    return object
  }

  /**
   * 移除实体对应的渲染对象并释放资源。
   *
   * @param entity - 目标实体
   */
  deleteEntity(entity: T) {
    const object = this.objectMap.get(entity.id)
    object?.parent?.remove(object)
    object && this.disposeObject(object)
    this.objectMap.delete(entity.id)
    this.hoverEntityIds.delete(entity.id)
    this.selectedEntityIds.delete(entity.id)
  }

  /** 移除所有实体对应的渲染对象 */
  deleteAllEntities() {
    this.dispose()
  }

  /**
   * 根据选项刷新实体的几何体与材质。
   *
   * @param entity - 目标实体
   * @param options - 刷新选项，默认仅重建几何体
   */
  updateEntity(
    entity: T,
    options: IUpdateOptions = { updateGeometry: true, updateMaterial: false }
  ) {
    if (options.updateGeometry) {
      this.onUpdateGeometry(entity)
    }
    if (options.updateMaterial) {
      this.onUpdateMaterial(entity)
    }
  }

  /**
   * 获取实体对应的渲染对象。
   *
   * @param entity - 目标实体
   * @returns 渲染对象；未创建时返回 undefined
   */
  getEntityObject(entity: T): U | undefined {
    return this.objectMap.get(entity.id)
  }

  protected abstract createObject(entity: T): U

  protected abstract onUpdateGeometry(entity: T): void

  protected abstract onUpdateMaterial(entity: T): void

  protected abstract disposeObject(object: U): void

  protected getCachedMaterial(entity: T) {
    return this.materialManager.getCachedMaterial(entity)
  }

  protected disposeMaterialFromObject(object: U) {
    const material = (object as any).material
    const entity = object.userData.entity as T
    if (material && entity) {
      this.materialManager.disposeMaterialByEntity(material, entity)
    }
  }

  /************ 高亮(预选、选中状态) ************/
  /**
   * 将实体置为预选高亮态，已选中或已预选的实体会被跳过。
   *
   * @param entity - 单个或多个目标实体
   */
  hover(entity: T | T[]) {
    const entities = Array.isArray(entity) ? entity : [entity]
    entities.forEach((entity) => {
      // 若实体已选中，则不进行预选高亮
      if (this.selectedEntityIds.has(entity.id) || this.hoverEntityIds.has(entity.id)) {
        return
      }
      this.hoverEntityIds.add(entity.id)
      this.handleEntityHighlight(entity, HighlightMode.Hover)
    })
  }

  /**
   * 取消实体的预选高亮；若实体同时处于选中态则保持选中。
   *
   * @param entity - 单个或多个目标实体
   */
  cancelHover(entity: T | T[]) {
    const entities = Array.isArray(entity) ? entity : [entity]
    entities.forEach((entity) => {
      this.hoverEntityIds.delete(entity.id)

      // 若实体已选中，则不进行预选高亮
      if (this.selectedEntityIds.has(entity.id)) {
        return
      }
      this.handleEntityHighlight(entity, HighlightMode.None)
    })
  }

  /**
   * 将实体置为选中态，已选中的实体会被跳过。
   *
   * @param entity - 单个或多个目标实体
   */
  select(entity: T | T[]) {
    const entities = Array.isArray(entity) ? entity : [entity]
    entities.forEach((entity) => {
      this.hoverEntityIds.delete(entity.id)
      if (this.selectedEntityIds.has(entity.id)) {
        return
      }
      this.selectedEntityIds.add(entity.id)
      this.handleEntityHighlight(entity, HighlightMode.Select)
    })
  }

  /**
   * 取消实体的选中态；若同时处于预选态则降级为 hover。
   *
   * @param entity - 单个或多个目标实体
   */
  cancelSelect(entity: T | T[]) {
    const entities = Array.isArray(entity) ? entity : [entity]
    entities.forEach((entity) => {
      this.selectedEntityIds.delete(entity.id)
      if (this.hoverEntityIds.has(entity.id)) {
        this.handleEntityHighlight(entity, HighlightMode.Hover)
        return
      } else {
        this.handleEntityHighlight(entity, HighlightMode.None)
      }
    })
  }

  protected abstract handleEntityHighlight(entity: T, mode: HighlightMode): void

  /**
   * 获取实体当前的高亮状态。
   *
   * @param entity - 目标实体
   * @returns Select/Hover/None 其中之一
   */
  getEntitySelectMode(entity: T): HighlightMode {
    if (this.selectedEntityIds.has(entity.id)) {
      return HighlightMode.Select
    } else if (this.hoverEntityIds.has(entity.id)) {
      return HighlightMode.Hover
    }
    return HighlightMode.None
  }

  protected updateEntityHighlight(entity: T) {
    const selectMode = this.getEntitySelectMode(entity)
    this.handleEntityHighlight(entity, selectMode)
  }

  /** 释放全部渲染对象、清空缓存并释放材质管理器 */
  dispose() {
    this.group.clear()
    this.objectMap.forEach((object) => {
      this.disposeObject(object)
    })
    this.objectMap.clear()
    this.hoverEntityIds.clear()
    this.selectedEntityIds.clear()
    this.materialManager.dispose()
  }
}
